import { getTodayDateString } from "../lib/date";
import { supabase } from "../lib/supabase";

export type LunchStatusValue = "home" | "order" | "no_food" | "eat_out";

export interface LunchStatusRecord {
  id: string;
  userEmail: string;
  status: LunchStatusValue;
  date: string;
  displayName?: string;
}

export interface OrderRecord {
  id: string;
  createdBy: string;
  restaurant: string;
  deadline: number; // Minutes from creation time
  date: string;
  link?: string; // URL to restaurant menu/ordering page
  status: 'open' | 'closed' | 'processing';
  dateEnd: string; // ISO timestamp when order expires
  createdAt: string; // ISO timestamp when order was created
  notes?: string;
  author?: string | null;
}

export interface OrderMemberRecord {
  id: string;
  orderId: string;
  userEmail: string;
  itemName?: string;
  userName?: string | null;
}

type Listener<T> = (payload: { action: "create" | "update" | "delete"; record: T }) => void;
const listeners = {
  lunchStatus: new Set<Listener<LunchStatusRecord>>(),
  orders: new Set<Listener<OrderRecord>>(),
  orderMembers: new Set<Listener<OrderMemberRecord>>()
};

// Global subscription state - prevent duplicate channel subscriptions
let globalChannels: Record<string, any> = {};
let realtimeInitialized = false;

async function ensureRealtime() {
  if (realtimeInitialized) {
    console.log('🔄 Realtime already initialized, skipping setup');
    return;
  }

  console.log('🚀 Initializing Supabase Realtime subscriptions...');
  
  // Setup lunch_status channel
  if (!globalChannels['lunch_status']) {
    console.log('🔗 Setting up lunch_status realtime subscription...');
    globalChannels['lunch_status'] = supabase
      .channel("lunch_status_global")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "lunch_status" },
        payload => {
          console.log('🔔 Lunch status realtime event received:', payload);
          const action = payload.eventType.toLowerCase() as "insert" | "update" | "delete";
          const record: any = (payload.new as any) || (payload.old as any);
          if (!record) {
            console.warn('⚠️ No record data in lunch status payload');
            return;
          }
          const mapped: LunchStatusRecord = {
            id: record.id,
            userEmail: record.useremail,
            status: record.status,
            date: record.date,
            displayName: record.displayname
          };
          const evtAction = action === "insert" ? "create" : (action as "update" | "delete");
          console.log(`📢 Broadcasting lunch status change to ${listeners.lunchStatus.size} listeners:`, { action: evtAction, record: mapped });
          listeners.lunchStatus.forEach(cb => cb({ action: evtAction, record: mapped }));
        }
      )
      .subscribe((status, error) => {
        console.log('📡 Lunch status subscription status:', status, error);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Lunch status realtime subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Lunch status subscription error:', error);
        }
      });
  }
  
  // Setup orders channel
  if (!globalChannels['orders']) {
    console.log('🔗 Setting up orders realtime subscription...');
    globalChannels['orders'] = supabase
      .channel("orders_global")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders" },
        payload => {
          console.log('🔔 Orders realtime event received:', payload);
          const action = payload.eventType.toLowerCase() as "insert" | "update" | "delete";
          const record: any = (payload.new as any) || (payload.old as any);
          if (!record) {
            console.warn('⚠️ No record data in orders payload');
            return;
          }
          const mapped: OrderRecord = {
            id: record.id,
            createdBy: record.createdby,
            restaurant: record.restaurant,
            deadline: parseInt(record.deadline) || 30,
            date: record.date,
            link: record.link,
            status: record.status,
            dateEnd: record.date_end,
    createdAt: record.created_at,
    notes: record.notes,
    author: record.author || null
          };
          const evtAction = action === "insert" ? "create" : (action as "update" | "delete");
          console.log(`📢 Broadcasting order change to ${listeners.orders.size} listeners:`, { action: evtAction, record: mapped });
          listeners.orders.forEach(cb => cb({ action: evtAction, record: mapped }));
        }
      )
      // Ensure presence to keep connection alive
      .on("presence", { event: "sync" }, () => {
        console.log('🤝 Orders presence sync');
      })
      .subscribe((status, error) => {
        console.log('📡 Orders subscription status:', status, error);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Orders realtime subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Orders subscription error:', error);
        }
      });
  }
  
  // Setup order_members channel
  if (!globalChannels['order_members']) {
    console.log('🔗 Setting up order_members realtime subscription...');
    globalChannels['order_members'] = supabase
      .channel("order_members_global")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "order_members" },
        payload => {
          console.log('🔔 Order members realtime event received:', payload);
          const action = payload.eventType.toLowerCase() as "insert" | "update" | "delete";
          const record: any = (payload.new as any) || (payload.old as any);
          if (!record) {
            console.warn('⚠️ No record data in order_members payload');
            return;
          }
          const mapped: OrderMemberRecord = {
            id: record.id,
            orderId: record.orderid,
            userEmail: record.useremail,
            itemName: record.itemname,
            userName: record.username || null
          };
          const evtAction = action === "insert" ? "create" : (action as "update" | "delete");
          console.log(`📢 Broadcasting order member change to ${listeners.orderMembers.size} listeners:`, { action: evtAction, record: mapped });
          listeners.orderMembers.forEach(cb => cb({ action: evtAction, record: mapped }));
        }
      )
      .on("presence", { event: "sync" }, () => {
        console.log('🤝 Order members presence sync');
      })
      .subscribe((status, error) => {
        console.log('📡 Order members subscription status:', status, error);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Order members realtime subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Order members subscription error:', error);
        }
      });
  }
  
  realtimeInitialized = true;
  console.log('✅ Realtime initialization completed');
}

export async function upsertLunchStatus(
  userEmail: string,
  status: LunchStatusValue,
  date: string = getTodayDateString(),
  displayName?: string
): Promise<LunchStatusRecord> {
  console.log('🔍 Calling upsertLunchStatus:', { userEmail, status, date });
  
  const { data: existing, error: findErr } = await supabase
    .from("lunch_status")
    .select("*")
    .eq("useremail", userEmail)
    .eq("date", date)
    .maybeSingle();
    
  console.log('📊 upsertLunchStatus find result:', { existing, findErr });
  
  if (findErr) {
    console.error('❌ Database error in upsertLunchStatus (find):', findErr);
    throw findErr;
  }
  if (existing) {
    console.log('🔄 Updating existing record:', existing.id);
    
    const { data, error } = await supabase
      .from("lunch_status")
      .update({ status })
      .eq("id", existing.id)
      .select("*")
      .single();
      
    console.log('📊 upsertLunchStatus update result:', { data, error });
    
    if (error) {
      console.error('❌ Database error in upsertLunchStatus (update):', error);
      throw error;
    }
    return {
      id: data.id,
      userEmail: data.useremail,
      status: data.status,
      date: data.date,
      displayName: data.displayname
    };
  } else {
    console.log('➕ Inserting new record for:', userEmail);
    
    const { data, error } = await supabase
      .from("lunch_status")
      .insert({ useremail: userEmail, status, date, displayname: displayName })
      .select("*")
      .single();
      
    console.log('📊 upsertLunchStatus insert result:', { data, error });
    
    if (error) {
      console.error('❌ Database error in upsertLunchStatus (insert):', error);
      throw error;
    }
    return {
      id: data.id,
      userEmail: data.useremail,
      status: data.status,
      date: data.date,
      displayName: data.displayname
    };
  }
}

export async function listLunchStatusesForDate(date: string): Promise<{
  home: LunchStatusRecord[];
  order: LunchStatusRecord[];
  byEmail: Record<string, LunchStatusRecord>;
}> {
  console.log('🔍 Calling listLunchStatusesForDate for date:', date);
  
  const { data, error } = await supabase
    .from("lunch_status")
    .select("*")
    .eq("date", date)
    .order("useremail", { ascending: true });
    
  console.log('📊 listLunchStatusesForDate result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in listLunchStatusesForDate:', error);
    throw error;
  }
  const records: LunchStatusRecord[] = (data || []).map(r => ({
    id: r.id,
    userEmail: r.useremail,
    status: r.status as LunchStatusValue,
    date: r.date,
    displayName: r.displayname
  }));
  const byEmail: Record<string, LunchStatusRecord> = {};
  for (const r of records) byEmail[r.userEmail] = r;
  return {
    home: records.filter(r => r.status === "home"),
    order: records.filter(r => r.status === "order"),
    byEmail
  };
}

export function subscribeLunchStatusesForDate(
  date: string,
  onChange: (payload: {
    action: "create" | "update" | "delete";
    record: LunchStatusRecord;
  }) => void
): () => void {
  const subscriberId = `lunch-status-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  console.log(`🎯 [${subscriberId}] Subscribing to lunch status changes for date:`, date);
  ensureRealtime();
  
  const cb: Listener<LunchStatusRecord> = payload => {
    console.log(`🎯 [${subscriberId}] Lunch status listener triggered:`, payload);
    if (payload.record.date !== date) {
      console.log(`⏭️ [${subscriberId}] Skipping lunch status change - wrong date:`, payload.record.date, 'vs', date);
      return;
    }
    console.log(`✅ [${subscriberId}] Processing lunch status change:`, payload);
    onChange(payload);
  };
  
  listeners.lunchStatus.add(cb);
  console.log(`📊 [${subscriberId}] Current lunch status listeners count:`, listeners.lunchStatus.size);
  
  // Update global counter for debugging
  (window as any).__lunchStatusListeners = listeners.lunchStatus.size;
  
  return () => {
    console.log(`🗑️ [${subscriberId}] Removing lunch status listener`);
    listeners.lunchStatus.delete(cb);
    console.log(`📊 [${subscriberId}] Remaining lunch status listeners:`, listeners.lunchStatus.size);
    (window as any).__lunchStatusListeners = listeners.lunchStatus.size;
  };
}

// Helper functions for order status and time
export function isOrderExpired(order: OrderRecord): boolean {
  return new Date() > new Date(order.dateEnd);
}

export function getRemainingMinutes(order: OrderRecord): number {
  const now = new Date();
  const endTime = new Date(order.dateEnd);
  const remainingMs = endTime.getTime() - now.getTime();
  return Math.max(0, Math.ceil(remainingMs / (1000 * 60)));
}

export function getFormattedTimeRemaining(order: OrderRecord): string {
  const minutes = getRemainingMinutes(order);
  if (minutes === 0) return "Đã hết hạn";
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${mins}m còn lại`;
  }
  return `${mins}m còn lại`;
}

export async function fetchOrdersWithFilters(filters: {
  date?: string;
  status?: 'open' | 'closed' | 'processing' | 'all';
  limit?: number;
}): Promise<OrderRecord[]> {
  console.log('🔍 Calling fetchOrdersWithFilters with filters:', filters);
  
  let query = supabase
    .from("orders")
    .select("*")
    .order("created_at", { ascending: false });
  
  if (filters.date) {
    query = query.eq("date", filters.date);
  }
  
  if (filters.status && filters.status !== 'all') {
    query = query.eq("status", filters.status);
  }
  
  if (filters.limit) {
    query = query.limit(filters.limit);
  }
  
  const { data, error } = await query;
  
  console.log('📊 fetchOrdersWithFilters result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in fetchOrdersWithFilters:', error);
    throw error;
  }
  
  if (!data || data.length === 0) return [];
  
  return data.map(r => ({
    id: r.id, 
    createdBy: r.createdby, 
    restaurant: r.restaurant, 
    deadline: parseInt(r.deadline) || 30, 
    date: r.date, 
    link: r.link, 
    status: r.status, 
    dateEnd: r.date_end, 
    createdAt: r.created_at,
    notes: r.notes,
    author: r.author || null
  }));
}

export async function fetchCurrentOrderForDate(date: string): Promise<OrderRecord | null> {
  console.log('🔍 Calling fetchCurrentOrderForDate for date:', date);
  
  const { data, error } = await supabase
    .from("orders")
    .select("*")
    .eq("date", date)
    .eq("status", "open")
    .order("created_at", { ascending: false })
    .limit(1);
    
  console.log('📊 fetchCurrentOrderForDate result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in fetchCurrentOrderForDate:', error);
    throw error;
  }
  if (!data || data.length === 0) return null;
  const r = data[0]; // First record since we ordered by created_at DESC
  return { 
    id: r.id, 
    createdBy: r.createdby, 
    restaurant: r.restaurant, 
    deadline: parseInt(r.deadline) || 30, 
    date: r.date, 
    link: r.link, 
    status: r.status, 
    dateEnd: r.date_end, 
    createdAt: r.created_at,
    notes: r.notes,
    author: r.author || null
  };
}

export async function fetchOrderById(orderId: string): Promise<OrderRecord | null> {
  console.log('🔍 Calling fetchOrderById for orderId:', orderId);
  
  const { data, error } = await supabase
    .from("orders")
    .select("*")
    .eq("id", orderId)
    .single();
    
  console.log('📊 fetchOrderById result:', { data, error });
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('❌ Database error in fetchOrderById:', error);
    throw error;
  }
  
  if (!data) return null;
  
  return { 
    id: data.id, 
    createdBy: data.createdby, 
    restaurant: data.restaurant, 
    deadline: parseInt(data.deadline) || 30, 
    date: data.date, 
    link: data.link, 
    status: data.status, 
    dateEnd: data.date_end, 
    createdAt: data.created_at,
    notes: data.notes,
    author: data.author || null
  };
}

export async function createOrder(params: {
  createdBy: string;
  restaurant: string;
  deadline: number; // Minutes from creation
  date?: string;
  link?: string;
  status?: 'open' | 'closed' | 'processing';
  notes?: string;
  authorName?: string;
}): Promise<OrderRecord> {
  console.log('🔍 Calling createOrder with params:', params);
  
  const now = new Date();
  const deadlineMs = params.deadline * 60 * 1000; // Convert minutes to milliseconds
  const dateEnd = new Date(now.getTime() + deadlineMs);
  
  const payload = {
    createdby: params.createdBy,
    restaurant: params.restaurant,
    deadline: params.deadline.toString(),
    date: params.date || getTodayDateString(),
    link: params.link,
    status: params.status || 'open',
    date_end: dateEnd.toISOString(),
    notes: params.notes,
    author: params.authorName || null
  };
  
  console.log('📤 Creating order with payload:', payload);
  
  const { data, error } = await supabase.from("orders").insert(payload).select("*").single();
  
  console.log('📊 createOrder result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in createOrder:', error);
    throw error;
  }
  return { 
    id: data.id, 
    createdBy: data.createdby, 
    restaurant: data.restaurant, 
    deadline: parseInt(data.deadline) || 30, 
    date: data.date, 
    link: data.link, 
    status: data.status, 
    dateEnd: data.date_end, 
    createdAt: data.created_at,
    notes: data.notes,
    author: data.author || null
  };
}

export async function updateOrder(params: {
  id: string;
  restaurant?: string;
  deadline?: number; // Minutes from creation
  link?: string;
  status?: 'open' | 'closed' | 'processing';
  notes?: string;
}): Promise<OrderRecord> {
  console.log('🔍 Calling updateOrder with params:', params);
  
  try {
    const updatePayload: any = {};
    if (params.restaurant !== undefined) updatePayload.restaurant = params.restaurant;
    if (params.link !== undefined) updatePayload.link = params.link;
    if (params.status !== undefined) updatePayload.status = params.status;
    if (params.notes !== undefined) updatePayload.notes = params.notes;
    
    if (params.deadline !== undefined) {
      updatePayload.deadline = params.deadline.toString();
      // Recalculate date_end if deadline changes
      // We need to get the original created_at first
      const { data: currentOrder } = await supabase
        .from("orders")
        .select("created_at")
        .eq("id", params.id)
        .single();
        
      if (currentOrder) {
        const createdAt = new Date(currentOrder.created_at);
        const deadlineMs = params.deadline * 60 * 1000;
        const newDateEnd = new Date(createdAt.getTime() + deadlineMs);
        updatePayload.date_end = newDateEnd.toISOString();
      }
    }
    
    console.log('📤 Updating order with payload:', updatePayload);
    
    const { data, error } = await supabase
      .from("orders")
      .update(updatePayload)
      .eq("id", params.id)
      .select("*")
      .single();
    
    console.log('📊 updateOrder result:', { data, error });
    
    if (error) {
      console.error('❌ Database error in updateOrder:', error);
      throw error;
    }
    
    const result = { 
      id: data.id, 
      createdBy: data.createdby, 
      restaurant: data.restaurant, 
      deadline: parseInt(data.deadline) || 30, 
      date: data.date, 
      link: data.link, 
      status: data.status, 
      dateEnd: data.date_end, 
      createdAt: data.created_at,
      notes: data.notes
    };
    console.log('✅ Order updated successfully:', result);
    return result;
    
  } catch (err) {
    console.error('❌ Error in updateOrder:', err);
    throw err;
  }
}

export async function deleteOrder(orderId: string): Promise<void> {
  console.log('🔍 Calling deleteOrder for orderId:', orderId);
  
  try {
    // First, delete all order members
    console.log('🗑️ Deleting order members...');
    const { error: membersError } = await supabase
      .from("order_members")
      .delete()
      .eq("orderid", orderId);
    
    if (membersError) {
      console.error('❌ Database error deleting order members:', membersError);
      throw membersError;
    }
    console.log('✅ Order members deleted successfully');
    
    // Then delete the order itself
    console.log('🗑️ Deleting order...');
    const { data, error } = await supabase
      .from("orders")
      .delete()
      .eq("id", orderId)
      .select(); // Get deleted data for confirmation
    
    console.log('📊 deleteOrder result:', { data, error });
    
    if (error) {
      console.error('❌ Database error in deleteOrder:', error);
      throw error;
    }
    
    console.log('✅ Order deleted successfully:', data);
    
  } catch (err) {
    console.error('❌ Error in deleteOrder:', err);
    throw err;
  }
}

export function subscribeOrdersForDate(
  date: string,
  onChange: (payload: { action: "create" | "update" | "delete"; record: OrderRecord }) => void
): () => void {
  const subscriberId = `orders-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  console.log(`🎯 [${subscriberId}] Subscribing to order changes for date:`, date);
  ensureRealtime();
  
  const cb: Listener<OrderRecord> = payload => {
    console.log(`🎯 [${subscriberId}] Orders listener triggered:`, payload);
    // Do not filter by date here; consumers may filter themselves. This avoids
    // dropping events where 'date' might be missing or different timezones.
    console.log(`✅ [${subscriberId}] Processing order change (no date filter):`, payload);
    onChange(payload);
  };
  
  listeners.orders.add(cb);
  console.log(`📊 [${subscriberId}] Current orders listeners count:`, listeners.orders.size);
  
  // Update global counter for debugging
  (window as any).__ordersListeners = listeners.orders.size;
  
  return () => {
    console.log(`🗑️ [${subscriberId}] Removing orders listener`);
    listeners.orders.delete(cb);
    console.log(`📊 [${subscriberId}] Remaining orders listeners:`, listeners.orders.size);
    (window as any).__ordersListeners = listeners.orders.size;
  };
}

export async function listOrderMembers(orderId: string): Promise<OrderMemberRecord[]> {
  console.log('🔍 Calling listOrderMembers for orderId:', orderId);
  
  const { data, error } = await supabase
    .from("order_members")
    .select("*")
    .eq("orderid", orderId)
    .order("created_at", { ascending: true });
    
  console.log('📊 listOrderMembers result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in listOrderMembers:', error);
    throw error;
  }
  return (data || []).map(r => ({ id: r.id, orderId: r.orderid, userEmail: r.useremail, itemName: r.itemname, userName: r.username || null }));
}

export async function listOrderMembersForDate(orderId: string, date: string): Promise<OrderMemberRecord[]> {
  console.log('🔍 Calling listOrderMembersForDate for orderId:', orderId, 'date:', date);
  
  // Get start and end of the specified date
  const startDate = new Date(date + 'T00:00:00.000Z');
  const endDate = new Date(date + 'T23:59:59.999Z');
  
  const { data, error } = await supabase
    .from("order_members")
    .select("*")
    .eq("orderid", orderId)
    .gte("created_at", startDate.toISOString())
    .lte("created_at", endDate.toISOString())
    .order("created_at", { ascending: true });
    
  console.log('📊 listOrderMembersForDate result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in listOrderMembersForDate:', error);
    throw error;
  }
  return (data || []).map(r => ({ id: r.id, orderId: r.orderid, userEmail: r.useremail, itemName: r.itemname, userName: r.username || null }));
}

export function subscribeOrderMembers(
  orderId: string,
  onChange: (payload: { action: "create" | "update" | "delete"; record: OrderMemberRecord }) => void
): () => void {
  const subscriberId = `order-members-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  console.log(`🎯 [${subscriberId}] Subscribing to order member changes for orderId:`, orderId);
  ensureRealtime();
  
  const cb: Listener<OrderMemberRecord> = payload => {
    console.log(`🎯 [${subscriberId}] Order members listener triggered:`, payload);
    // For delete events, some Supabase setups only include primary key fields.
    // If orderId is missing, still propagate so the UI can refetch this order's members.
    if (payload.action !== 'delete' && payload.record.orderId !== orderId) {
      console.log(`⏭️ [${subscriberId}] Skipping order member change - wrong orderId:`, payload.record.orderId, 'vs', orderId);
      return;
    }
    console.log(`✅ [${subscriberId}] Processing order member change:`, payload);
    onChange(payload);
  };
  
  listeners.orderMembers.add(cb);
  console.log(`📊 [${subscriberId}] Current order members listeners count:`, listeners.orderMembers.size);
  
  // Update global counter for debugging
  (window as any).__orderMembersListeners = listeners.orderMembers.size;
  
  return () => {
    console.log(`🗑️ [${subscriberId}] Removing order members listener`);
    listeners.orderMembers.delete(cb);
    console.log(`📊 [${subscriberId}] Remaining order members listeners:`, listeners.orderMembers.size);
    (window as any).__orderMembersListeners = listeners.orderMembers.size;
  };
}

export async function joinOrder(orderId: string, userEmail: string, itemName?: string, userName?: string): Promise<OrderMemberRecord> {
  console.log('🔍 Calling joinOrder:', { orderId, userEmail });
  
  const { data: existing, error: findErr } = await supabase
    .from("order_members")
    .select("*")
    .eq("orderid", orderId)
    .eq("useremail", userEmail)
    .maybeSingle();
    
  console.log('📊 joinOrder find result:', { existing, findErr });
  
  if (findErr) {
    console.error('❌ Database error in joinOrder (find):', findErr);
    throw findErr;
  }
  if (existing) {
    console.log('✅ User already joined order');
    return { id: existing.id, orderId: existing.orderid, userEmail: existing.useremail, itemName: existing.itemname };
  }
  
  console.log('➕ Adding user to order');
  
  const { data, error } = await supabase
    .from("order_members")
    .insert({ orderid: orderId, useremail: userEmail, itemname: itemName, username: userName ?? userEmail.split('@')[0] })
    .select("*")
    .single();
    
  console.log('📊 joinOrder insert result:', { data, error });
  
  if (error) {
    console.error('❌ Database error in joinOrder (insert):', error);
    throw error;
  }
  return { id: data.id, orderId: data.orderid, userEmail: data.useremail, itemName: data.itemname, userName: data.username || null };
}

export async function leaveOrder(orderId: string, userEmail: string): Promise<void> {
  console.log('🔍 Calling leaveOrder:', { orderId, userEmail });
  
  const { data: existing, error: findErr } = await supabase
    .from("order_members")
    .select("id")
    .eq("orderid", orderId)
    .eq("useremail", userEmail)
    .maybeSingle();
    
  console.log('📊 leaveOrder find result:', { existing, findErr });
  
  if (findErr) {
    console.error('❌ Database error in leaveOrder (find):', findErr);
    throw findErr;
  }
  if (!existing?.id) {
    console.log('ℹ️ User not in order, nothing to leave');
    return;
  }
  
  console.log('➖ Removing user from order');
  
  const { error } = await supabase.from("order_members").delete().eq("id", existing.id);
  
  console.log('📊 leaveOrder delete result:', { error });
  
  if (error) {
    console.error('❌ Database error in leaveOrder (delete):', error);
    throw error;
  }
}

// Using plain mapping above for Supabase rows

export async function updateOrderMemberItem(
  orderId: string,
  userEmail: string,
  itemName: string | null
): Promise<OrderMemberRecord> {
  console.log('🔍 Calling updateOrderMemberItem:', { orderId, userEmail, itemName });

  const { data: existing, error: findErr } = await supabase
    .from("order_members")
    .select("*")
    .eq("orderid", orderId)
    .eq("useremail", userEmail)
    .maybeSingle();

  if (findErr) {
    console.error('❌ Database error in updateOrderMemberItem (find):', findErr);
    throw findErr;
  }
  if (!existing?.id) {
    throw new Error('Order member not found');
  }

  const { data, error } = await supabase
    .from("order_members")
    .update({ itemname: itemName })
    .eq("id", existing.id)
    .select("*")
    .single();

  if (error) {
    console.error('❌ Database error in updateOrderMemberItem (update):', error);
    throw error;
  }

  const result: OrderMemberRecord = {
    id: data.id,
    orderId: data.orderid,
    userEmail: data.useremail,
    itemName: data.itemname
  };
  console.log('✅ Updated order member item:', result);
  return result;
}

// Debug function to check all listeners and channels
export const debugAllListeners = () => {
  console.log('🔍 Debugging All Listeners & Channels:');
  console.log('🎯 Global Subscription State:');
  console.log('  - Realtime Initialized:', realtimeInitialized);
  console.log('  - Global Channels:', Object.keys(globalChannels));
  
  console.log('📊 Listener Counts:');
  console.log('  - Lunch Status:', listeners.lunchStatus.size);
  console.log('  - Orders:', listeners.orders.size);
  console.log('  - Order Members:', listeners.orderMembers.size);
  
  console.log('🏷️ Channel States:');
  Object.entries(globalChannels).forEach(([name, channel]) => {
    if (channel) {
      console.log(`  - ${name}:`, {
        state: (channel as any).state,
        topic: (channel as any).topic,
        bindings: (channel as any).bindings?.length || 0
      });
    }
  });
  
  // Check Supabase realtime status too
  const realtime = (supabase as any).realtime;
  if (realtime) {
    console.log('🔗 Supabase Realtime:');
    console.log('  - Connection State:', realtime.connection?.connectionState);
    console.log('  - Active Channels:', Object.keys(realtime.channels || {}));
  }
};

// Export for global access
(window as any).debugAllListeners = debugAllListeners;
